import {memo, useEffect, useMemo, useState} from "react";
import {useDroppable} from "@dnd-kit/core";
import {useTranslation} from "react-i18next";
import {keyframes} from "@emotion/react";
import chroma from "chroma-js";
import ListAltIcon from "@mui/icons-material/ListAlt";
import PersonOutline from "@mui/icons-material/PersonOutline";
import ChildCare from "@mui/icons-material/ChildCare";
import AddReservationButton from "./AddReservationButton";
import {
    Reservation
} from "crqlar-react-common/dist/models/reservation/reservation.model";
import {DragData, TableOverlay} from "../types";
import {useFloorplanContext} from "../hooks/useFloorplanContext";
import TableOverlayCardBody from "./TableOverlayCardBody";
import DraggableTableCard from "./DraggableTableCard";
import ViewTableReservationsDialog from "./dialogs/ViewTableReservationsDialog";
import {
    FloorplanReservationItemDTO
} from "crqlar-react-common/dist/models/floorplan/floorplanReservationItem.dto";
import {
    isReservationOngoingAt,
    isReservationUpcomingAt,
    reservationIntersectsTimeframe,
    useTimeframe
} from "../hooks/useTimeframe";
import {
    getFloorplanGuestCount,
    getFloorplanGuestCounts,
    getReservationDisplay,
} from "../utils/reservation-utils";
import {useReadStore} from "../hooks/useReadStore";
import {useTableDropState} from "../hooks/useTableDropState";
import {FABRIC_DEFAULTS} from "../utils/fabric-defaults";
import {
    DRAG_BG_FULL,
    DRAG_BG_OK,
    DRAG_BORDER_FULL,
    DRAG_BORDER_OK,
    OVER_BG_FULL,
    OVER_BG_OK,
    OVER_BORDER_FULL,
    OVER_BORDER_OK
} from "../utils/constants";
import {Box, Stack} from "@mui/material";
import TableOverlayActionButton from "./TableOverlayActionButton";
import FloorplanReservationTooltip from "./FloorplanReservationTooltip";
import {resolveAdultChildGuestCounts} from "../../../../common/ageCategoryDefaults";

// Part 1: ring scales in from far away and lands on the table
// Part 1: ring scales in and stays fully visible
const highlightScaleIn = keyframes`
    0% {
        outline-color: rgba(220, 38, 38, 1);
        outline-offset: 50px;
        outline-width: 2px;
    }
    100% {
        outline-color: rgba(220, 38, 38, 1);
        outline-offset: 4px;
        outline-width: 5px;
    }
`;



interface Props {
    overlay: TableOverlay;
    activeDragData: DragData | null;
    disableTableCardDrag?: boolean;
    tableReservations: FloorplanReservationItemDTO[];
    reservationTooltipActive: boolean;
    onReservationTooltipActiveChange: (tableId: string, active: boolean) => void;
    onClickEdit?: (reservation: Reservation) => void;
}

const TableOverlayLayerItem: React.FC<Props> = ({
                                                    overlay,
                                                    activeDragData,
                                                    disableTableCardDrag = false,
                                                    tableReservations,
                                                    reservationTooltipActive,
                                                    onReservationTooltipActiveChange,
                                                    onClickEdit,
                                                }) => {
    const {timeframeStart, timeframeEnd, referenceTime} = useTimeframe();
    const {showSeatsAroundTable, restaurantDraft} = useFloorplanContext();
    const floorplanReservationTooltipEnabled =
        restaurantDraft?.meta?.floorplanReservationTooltipEnabled !== false;
    const {setNodeRef, isOver} = useDroppable({id: overlay.id});
    const [isHovered, setIsHovered] = useState(false);
    const [dialogOpen, setDialogOpen] = useState(false);
    const highlightedTableIds = useReadStore(s => s.highlightedTableIds);
    const isHighlighted = highlightedTableIds.has(overlay.id);

    // find highlight
    const [highlightKey, setHighlightKey] = useState(0);
    useEffect(() => {
        if (isHighlighted) setHighlightKey(k => k + 1);
    }, [isHighlighted]);

    const combinedRef = setNodeRef;

    // reservations
    const visibleReservations = useMemo(() => {
        return tableReservations.filter((item) => reservationIntersectsTimeframe(
            item.restaurantReservation?.expectedStartDate,
            item.restaurantReservation?.expectedEndDate,
            timeframeStart,
            timeframeEnd,
        ));
    }, [tableReservations, timeframeStart, timeframeEnd]);

    const currentReservation = useMemo(() => {
        if (!visibleReservations.length) return undefined;

        const ongoing = visibleReservations.find((item) => isReservationOngoingAt(
            item.restaurantReservation?.expectedStartDate,
            item.restaurantReservation?.expectedEndDate,
            referenceTime,
        ));
        if (ongoing) return ongoing;

        return visibleReservations.find((item) => isReservationUpcomingAt(
            item.restaurantReservation?.expectedStartDate,
            referenceTime,
        ));
    }, [visibleReservations, referenceTime]);

    // DND
    const {isDragActive, dragOk, hasRoom, hasTimeConflict} = useTableDropState(
        activeDragData, overlay, tableReservations, visibleReservations,
    );

    // UI
    const {
        borderColor: reservationBorderColor,
        backgroundColor: reservationBackgroundColor,
    } = getReservationDisplay({
        reservation: currentReservation?.restaurantReservation,
        profiles: currentReservation?.profiles,
        ageCategories: currentReservation?.ageCategories,
        guestCounts: getFloorplanGuestCounts(currentReservation),
    });

    const pad = isDragActive ? 5 : 0;

    // The label sits directly on the canvas table body, so it has to follow whatever that body shows:
    // ReadCanvasView repaints the body with reservation status colours (seat mode with a current
    // reservation), and only outside that case does the table keep the fill and text colour configured
    // in the floorplan editor.
    const bodyKeepsConfiguredFill = !(showSeatsAroundTable && currentReservation);
    const labelColor = bodyKeepsConfiguredFill
        ? (overlay.textColor ?? FABRIC_DEFAULTS.table.textColor)
        : undefined;

    let borderColor = (!showSeatsAroundTable && currentReservation && reservationBorderColor)
        ? chroma(reservationBorderColor).alpha(0.35).css()
        : 'transparent';
    let backgroundColor = (!showSeatsAroundTable && currentReservation && reservationBackgroundColor)
        ? chroma(reservationBackgroundColor).alpha(0.35).css()
        : 'transparent';
    if (isDragActive) {
        borderColor = dragOk ? DRAG_BORDER_OK : DRAG_BORDER_FULL;
        backgroundColor = dragOk ? DRAG_BG_OK : DRAG_BG_FULL;
    }
    if (isOver) {
        borderColor = dragOk ? OVER_BORDER_OK : OVER_BORDER_FULL;
        backgroundColor = dragOk ? OVER_BG_OK : OVER_BG_FULL;
    }

    const handleViewClick = () => {
        setIsHovered(false);
        setDialogOpen(true);
    };

    const hoverBridgeY = {
        top: 32,
        bottom: 0,
    };
    const useOverlayHoverTarget = !currentReservation && tableReservations.length === 0;

    return (
        <>
            <Box
                ref={combinedRef}
                key={highlightKey}
                data-testid={`table-overlay-${overlay.id}`}
                onMouseEnter={useOverlayHoverTarget ? () => setIsHovered(true) : undefined}
                onMouseLeave={useOverlayHoverTarget ? () => setIsHovered(false) : undefined}
                sx={{
                    position: 'absolute',
                    left: overlay.x - pad,
                    top: overlay.y - pad,
                    width: overlay.width + pad * 2,
                    height: overlay.height + pad * 2,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    pointerEvents: 'auto',
                    zIndex: isHighlighted ? 300 : isHovered ? 200 : 2,
                    overflow: 'visible',
                    border: `2px solid ${borderColor}`,
                    borderRadius: '8px',
                    background: backgroundColor,
                    boxSizing: 'border-box',
                    transition: 'border-color 0.2s, background-color 0.2s',
                    ...(isHighlighted && {
                        outline: '2px solid rgba(220, 38, 38, 1)',
                        outlineOffset: '50px',
                        animation: `${highlightScaleIn} 0.6s cubic-bezier(0.33, 0, 0.2, 1) forwards`,
                    }),
                }}

            >
                <TableInfo
                    overlay={overlay}
                    currentReservation={currentReservation}
                    visibleReservations={visibleReservations}
                    labelColor={labelColor}
                />

                <div
                    onMouseEnter={!useOverlayHoverTarget ? () => setIsHovered(true) : undefined}
                    onMouseLeave={!useOverlayHoverTarget ? () => setIsHovered(false) : undefined}
                    style={{
                        position: 'relative',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        paddingTop: hoverBridgeY.top,
                        paddingBottom: hoverBridgeY.bottom,
                        marginTop: -hoverBridgeY.top,
                        marginBottom: -hoverBridgeY.bottom,
                    }}
                >
                    {currentReservation ? (
                        <div style={{position: 'relative'}}>
                            {isDragActive && !dragOk && (
                                <LabelConflict hasRoom={hasRoom}
                                               hasTimeConflict={hasTimeConflict}/>
                            )}
                            <CurrentReservation
                                overlay={overlay}
                                currentReservation={currentReservation}
                                isHovered={isHovered}
                                tooltipDisabled={isDragActive || !floorplanReservationTooltipEnabled}
                                disableTableCardDrag={disableTableCardDrag}
                                tableReservationCount={tableReservations.length}
                                reservationTooltipActive={reservationTooltipActive}
                                onReservationTooltipActiveChange={onReservationTooltipActiveChange}
                                onClickEdit={onClickEdit}
                                onViewClick={handleViewClick}
                            />
                        </div>
                    ) : (
                        <>
                            {isDragActive && !dragOk && (
                                <LabelConflict hasRoom={hasRoom}
                                               hasTimeConflict={hasTimeConflict}
                                               centered/>
                            )}
                            <EmptyReservation
                                overlay={overlay}
                                isHovered={isHovered}
                                tableReservationCount={tableReservations.length}
                                onViewClick={handleViewClick}
                            />
                        </>
                    )}
                </div>
            </Box>

            <ViewTableReservationsDialog
                open={dialogOpen}
                overlay={overlay}
                tableId={overlay.id}
                reservations={tableReservations}
                onClickEdit={onClickEdit}
                onClose={() => setDialogOpen(false)}
            />
        </>
    );
};

export default memo(TableOverlayLayerItem);


function LabelConflict({hasRoom, hasTimeConflict, centered}: {
    hasRoom: boolean;
    hasTimeConflict: boolean;
    centered?: boolean;
}) {
    const {t} = useTranslation();

    const both = !hasRoom && hasTimeConflict;

    return (
        <div
            style={{
                position: 'absolute',
                ...(centered
                    ? {top: '50%', left: '50%', transform: 'translate(-50%, -50%)'}
                    : {bottom: '100%', left: '50%', transform: 'translateX(-50%)', marginBottom: 4}
                ),
                fontSize: 10,
                lineHeight: '14px',
                fontWeight: 600,
                color: DRAG_BORDER_FULL,
                background: '#fff',
                padding: '3px 8px',
                borderRadius: 4,
                border: `1px solid ${DRAG_BORDER_FULL}`,
                textAlign: 'center',
                whiteSpace: 'nowrap',
                pointerEvents: 'none',
                boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
            }}
        >
            {both ? (<>
                {t('Not enough space')}
                <br/>
                +&nbsp;{t('Time overlap')}
            </>) : !hasRoom
                ? t('Not enough space')
                : t('Time overlap')}
        </div>
    );
}

function TableInfo({overlay, currentReservation, visibleReservations, labelColor}: {
    overlay: TableOverlay;
    currentReservation: FloorplanReservationItemDTO | undefined;
    visibleReservations: FloorplanReservationItemDTO[];
    labelColor?: string;
}) {
    const {
        adultCount: adultsFromBreakdown,
        childCount: childrenUnder18FromBreakdown,
    } = resolveAdultChildGuestCounts(
        currentReservation?.ageCategories ?? [],
        getFloorplanGuestCounts(currentReservation),
    );

    const currentGuestCount = visibleReservations.reduce(
        (sum, item) => sum + getFloorplanGuestCount(item),
        0,
    );

    // For multi-table reservations, distribute guests across tables and show
    // only this table's share (no age breakdown since we can't know who sits where)
    const tableResources = currentReservation?.tableResources ?? [];
    const isMultiTable = tableResources.length > 1;

    let displayAdults: number;
    let displayChildren: number;

    if (isMultiTable) {
        const totalGuests = getFloorplanGuestCount(currentReservation);
        let remaining = totalGuests;
        const tableIndex = tableResources.findIndex(tr => tr.id === overlay.id);
        for (let i = 0; i < tableIndex && i < tableResources.length; i++) {
            const prevCapacity = tableResources[i].capacity ?? 4;
            remaining -= Math.min(remaining, prevCapacity);
        }
        displayAdults = Math.min(remaining, overlay.capacity);
        displayChildren = 0;
    } else {
        const hasDetailedBreakdown = adultsFromBreakdown > 0 || childrenUnder18FromBreakdown > 0;
        displayAdults = hasDetailedBreakdown ? adultsFromBreakdown : currentGuestCount;
        displayChildren = childrenUnder18FromBreakdown;
    }

    const hasAgeBreakdown = displayAdults > 0 || displayChildren > 0;

    return (
        <span
            style={{
                whiteSpace: 'nowrap',
                padding: '2px 4px',
                userSelect: 'none',
                color: labelColor,
                fontSize: 12,
                lineHeight: '16px',
                pointerEvents: 'none',
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                transform: overlay.angle ? `rotate(${overlay.angle}deg)` : undefined,
                transformOrigin: 'center center',
            }}
        >
            <strong style={{marginRight: '0.35rem'}}>
                {overlay.resource.name}-{overlay.capacity}
            </strong>
            {hasAgeBreakdown && (
                <span
                    style={{display: 'inline-flex', alignItems: 'center', gap: 6}}>
                    {displayAdults > 0 && (
                        <span style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 2
                        }}>
                            <PersonOutline
                                sx={{fontSize: 12, color: labelColor ?? 'text.secondary'}}/>
                            {displayAdults}
                        </span>
                    )}
                    {displayChildren > 0 && (
                        <span style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 2
                        }}>
                            <ChildCare
                                sx={{fontSize: 12, color: labelColor ?? 'text.secondary'}}/>
                            {displayChildren}
                        </span>
                    )}
                </span>
            )}
        </span>
    );
}


function CurrentReservation({
                                overlay,
                                currentReservation,
                                isHovered,
                                tooltipDisabled,
                                disableTableCardDrag,
                                tableReservationCount,
                                reservationTooltipActive,
                                onReservationTooltipActiveChange,
                                onClickEdit,
                                onViewClick
                            }: {
    overlay: TableOverlay;
    currentReservation: FloorplanReservationItemDTO;
    isHovered: boolean;
    tooltipDisabled: boolean;
    disableTableCardDrag: boolean;
    tableReservationCount: number;
    reservationTooltipActive: boolean;
    onReservationTooltipActiveChange: (tableId: string, active: boolean) => void;
    onClickEdit?: (reservation: Reservation) => void;
    onViewClick: () => void;
}) {
    const {t} = useTranslation();


    return (
        <div style={{position: 'relative'}}>
            {/* Actions */}
            <div style={{
                position: 'absolute',
                top: 0,
                left: '50%',
                transform: isHovered
                    ? 'translate(-50%, calc(-100% - 4px))'
                    : 'translate(-50%, 0)',
                opacity: isHovered ? 1 : 0,
                pointerEvents: isHovered ? 'auto' : 'none',
            }}>
                <Stack direction="row" gap={0.25}>
                    <AddReservationButton resource={overlay.resource}/>
                    {tableReservationCount > 0 && (
                        <TableOverlayActionButton
                            label={t("View")}
                            onClick={onViewClick}
                            showLabel
                        >
                            <ListAltIcon sx={{fontSize: 18}}/>
                        </TableOverlayActionButton>
                    )}
                </Stack>
            </div>

            <FloorplanReservationTooltip
                reservationItem={currentReservation}
                tableName={overlay.resource.name}
                disabled={tooltipDisabled}
                active={reservationTooltipActive}
                onOpen={() => onReservationTooltipActiveChange(overlay.id, true)}
                onClose={() => onReservationTooltipActiveChange(overlay.id, false)}
                onOpenReservation={
                    onClickEdit
                        ? () => onClickEdit(currentReservation.restaurantReservation)
                        : undefined
                }
            >
                <DraggableTableCard
                    id={`table-card-${overlay.id}-${currentReservation.reservationId}`}
                    reservation={currentReservation}
                    sourceTableId={overlay.id}
                    disabled={disableTableCardDrag}
                >
                    <TableOverlayCardBody
                        reservation={currentReservation.restaurantReservation}
                        guestCounts={getFloorplanGuestCounts(currentReservation)}
                        profiles={currentReservation.profiles}
                        accommodationReservations={currentReservation.accommodationReservations}
                        accommodationResources={currentReservation.accommodationResources}
                        tableReservationCount={tableReservationCount}
                        onClickEdit={() => onClickEdit?.(currentReservation.restaurantReservation)}
                        onClickView={onViewClick}
                    />
                </DraggableTableCard>
            </FloorplanReservationTooltip>

        </div>
    );
}

function EmptyReservation({
                              overlay,
                              isHovered,
                              tableReservationCount,
                              onViewClick
                          }: {
    overlay: TableOverlay;
    isHovered: boolean;
    tableReservationCount: number;
    onViewClick: () => void;
}) {
    const {t} = useTranslation();

    // Has reservations outside current timeframe — always show View button
    if (tableReservationCount > 0) {
        return (
            <div style={{position: 'relative'}}>
                {/* Add action */}
                <div style={{
                    position: 'absolute',
                    top: 0,
                    left: '50%',
                    transform: isHovered
                        ? 'translate(-50%, calc(-100% - 4px))'
                        : 'translate(-50%, 0)',
                    opacity: isHovered ? 1 : 0,
                    pointerEvents: isHovered ? 'auto' : 'none',
                }}>
                    <AddReservationButton resource={overlay.resource}/>
                </div>

                <TableOverlayActionButton
                    label={t("View")}
                    onClick={onViewClick}
                    showLabel
                >
                    <ListAltIcon sx={{fontSize: 18}}/>
                </TableOverlayActionButton>
            </div>
        );
    }

    // Truly empty — show Add on hover only
    return (
        <div style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 4,
            opacity: isHovered ? 1 : 0,
            pointerEvents: isHovered ? 'auto' : 'none',
        }}
        >
            <AddReservationButton resource={overlay.resource}/>
        </div>
    );
}
